import { useContext, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Flex,
  Stack,
  Grid,
  Column,
  Box,
} from "@twilio-paste/core";
import { MicrophoneOnIcon } from "@twilio-paste/icons/cjs/MicrophoneOnIcon";
import { MicrophoneOffIcon } from "@twilio-paste/icons/cjs/MicrophoneOffIcon";
import { TwilioVoiceContext } from "../WebsocketManagers/VoiceManager";
import DTMFButton from "./DtmfButton";
import { addDigitToDestinationNumber } from "../../actions";
import CallStatusMessage from "./StatusMessage";

const actionBtn = {
  width: "100%",
  height: "3.4rem",
  fontSize: "0.95rem",
  fontWeight: "600",
  fontFamily: "inherit",
  borderRadius: "16px",
  cursor: "pointer",
  letterSpacing: "0.05em",
  transition: "all 0.18s ease",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
};

const callBtnStyle = {
  ...actionBtn,
  background: "linear-gradient(135deg, rgba(2, 99, 224, 0.95) 0%, rgba(2, 80, 190, 0.9) 100%)",
  color: "#fff",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  boxShadow: "0 4px 16px rgba(2, 99, 224, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
};

const callBtnHover = {
  boxShadow: "0 6px 24px rgba(2, 99, 224, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.25)",
};

const hangUpBtnStyle = {
  ...actionBtn,
  background: "linear-gradient(135deg, rgba(168, 14, 14, 0.15) 0%, rgba(140, 10, 10, 0.1) 100%)",
  color: "rgba(185, 15, 9, 0.84)",
  border: "1px solid rgba(168, 14, 14, 0.2)",
  boxShadow: "0 2px 8px rgba(168, 14, 14, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
};

const hangUpBtnHover = {
  background: "linear-gradient(135deg, rgba(168, 14, 14, 0.22) 0%, rgba(140, 10, 10, 0.17) 100%)",
  boxShadow: "0 4px 16px rgba(168, 14, 14, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.7)",
};

const disabledOverride = {
  opacity: 0.35,
  cursor: "not-allowed",
  boxShadow: "none",
};

const muteBtnStyle = {
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  border: "1px solid rgba(2, 99, 224, 0.12)",
  background: "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(230,240,255,0.6) 100%)",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  boxShadow: "0 1px 4px rgba(2, 99, 224, 0.08), inset 0 1px 0 rgba(255,255,255,0.8)",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.15s ease",
  padding: 0,
};

const dtmfDisplayStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 16px",
  borderRadius: "14px",
  background: "linear-gradient(135deg, rgba(240, 247, 255, 0.85) 0%, rgba(225, 238, 255, 0.55) 100%)",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  border: "1px solid rgba(2, 99, 224, 0.1)",
  animation: "dtmfSlideIn 0.2s ease-out",
};

const dtmfDigitsStyle = {
  flex: 1,
  fontSize: "1.6rem",
  fontWeight: "400",
  color: "rgb(20, 28, 45)",
  letterSpacing: "0.12em",
  textAlign: "center",
  lineHeight: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const dtmfLabelStyle = {
  fontSize: "0.6rem",
  fontWeight: "500",
  color: "rgba(2, 99, 224, 0.45)",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  textAlign: "center",
  marginTop: "4px",
};

const dtmfClearBtnStyle = {
  width: "28px",
  height: "28px",
  borderRadius: "50%",
  border: "1px solid rgba(2, 99, 224, 0.12)",
  background: "rgba(255,255,255,0.7)",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  transition: "all 0.15s ease",
  flexShrink: 0,
};

const PhoneIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const HangUpIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

function ActionButton({ style, hoverStyle, disabled, onClick, children }) {
  return (
    <button
      style={disabled ? { ...style, ...disabledOverride } : style}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (!disabled && hoverStyle) {
          Object.assign(e.currentTarget.style, hoverStyle);
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          Object.entries(style).forEach(([k, v]) => {
            if (hoverStyle && k in hoverStyle) e.currentTarget.style[k] = v;
          });
          e.currentTarget.style.transform = "scale(1)";
        }
      }}
      onMouseDown={(e) => { if (!disabled) e.currentTarget.style.transform = "scale(0.97)"; }}
      onMouseUp={(e) => { if (!disabled) e.currentTarget.style.transform = "scale(1)"; }}
    >
      {children}
    </button>
  );
}

function Dialer() {
  const currentCallInfo = useSelector((state) => state.currentCallInfo);
  const destinationNumber = useSelector((state) => state.destinationNumber);
  const isMuted = useSelector((state) => state.isMuted);
  const dispatch = useDispatch();

  const [dtmfInput, setDtmfInput] = useState("");
  const [dtmfFading, setDtmfFading] = useState(false);
  const dtmfTimeoutRef = useRef(null);

  const dialer = useContext(TwilioVoiceContext);
  const { acceptCall, voiceDevice } = dialer;

  useEffect(() => {
    if (!currentCallInfo) {
      setDtmfInput("");
      setDtmfFading(false);
    }
  }, [currentCallInfo]);

  function fadeAndClearDtmf() {
    setDtmfFading(true);
    setTimeout(() => {
      setDtmfInput("");
      setDtmfFading(false);
    }, 300);
  }

  function makeCall() {
    dialer.makeCall(destinationNumber);
  }

  function hangUp() {
    dialer.hangUp();
  }

  function toggleMute() {
    dialer.toggleMute();
  }

  function sendDTMF(num) {
    if (!currentCallInfo) {
      dispatch(addDigitToDestinationNumber(num));
    } else {
      dialer.sendDTMF(num);
      setDtmfFading(false);
      const next = dtmfInput + num;
      setDtmfInput(next);
      if (dtmfTimeoutRef.current) clearTimeout(dtmfTimeoutRef.current);
      if (next.length >= 4) {
        dtmfTimeoutRef.current = setTimeout(() => fadeAndClearDtmf(), 1000);
      } else {
        dtmfTimeoutRef.current = setTimeout(() => fadeAndClearDtmf(), 8000);
      }
    }
  }

  function clearDtmfInput() {
    setDtmfInput("");
    setDtmfFading(false);
    if (dtmfTimeoutRef.current) clearTimeout(dtmfTimeoutRef.current);
  }

  function generateDTMFRow(row) {
    return row.map((tone) => (
      <DTMFButton
        key={tone}
        tone={tone}
        fullWidth={true}
        onClick={() => sendDTMF(tone)}
      />
    ));
  }

  const isCallInProgress = !!currentCallInfo;
  const isIncomingCall =
    acceptCall && currentCallInfo && currentCallInfo._direction === "INCOMING";

  return (
    <Box width="100%" paddingTop="space60">
      <Stack orientation="vertical" spacing="space60">
        <Box width="100%">
          <Flex vAlignContent="center">
            <Flex grow hAlignContent={"center"}>
              <CallStatusMessage
                voiceDevice={voiceDevice}
                currentCallInfo={currentCallInfo}
              />
            </Flex>
            <Flex>
              {isCallInProgress && (
                <button
                  style={muteBtnStyle}
                  onClick={toggleMute}
                  title={isMuted ? "Unmute" : "Mute"}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "rgba(2, 99, 224, 0.25)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(2, 99, 224, 0.14), inset 0 1px 0 rgba(255,255,255,0.9)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(2, 99, 224, 0.12)";
                    e.currentTarget.style.boxShadow = "0 1px 4px rgba(2, 99, 224, 0.08), inset 0 1px 0 rgba(255,255,255,0.8)";
                  }}
                >
                  {!isMuted ? (
                    <MicrophoneOnIcon
                      size="sizeIcon20"
                      title="Mute"
                      decorative={false}
                    />
                  ) : (
                    <MicrophoneOffIcon
                      size="sizeIcon20"
                      title="Unmute"
                      decorative={false}
                    />
                  )}
                </button>
              )}
            </Flex>
          </Flex>

          {isCallInProgress && dtmfInput && (
            <>
              <style>{`
                @keyframes dtmfSlideIn {
                  from { opacity: 0; transform: translateY(-6px); }
                  to { opacity: 1; transform: translateY(0); }
                }
                @keyframes dtmfSlideOut {
                  from { opacity: 1; transform: translateY(0); }
                  to { opacity: 0; transform: translateY(-6px); }
                }
              `}</style>
              <Box paddingTop="space40" paddingBottom="space20">
                <div style={{
                  ...dtmfDisplayStyle,
                  animation: dtmfFading ? "dtmfSlideOut 0.3s ease-in forwards" : "dtmfSlideIn 0.2s ease-out",
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={dtmfDigitsStyle}>{dtmfInput}</div>
                    <div style={dtmfLabelStyle}>DTMF Input</div>
                  </div>
                  <button
                    style={dtmfClearBtnStyle}
                    onClick={clearDtmfInput}
                    title="Clear"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "rgba(2, 99, 224, 0.25)";
                      e.currentTarget.style.background = "rgba(240, 247, 255, 0.9)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "rgba(2, 99, 224, 0.12)";
                      e.currentTarget.style.background = "rgba(255,255,255,0.7)";
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(2,99,224,0.5)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              </Box>
            </>
          )}

          <Box paddingTop="space30" paddingBottom="space30">
            <Flex wrap>{generateDTMFRow(["1", "2", "3"])}</Flex>
            <Flex wrap>{generateDTMFRow(["4", "5", "6"])}</Flex>
            <Flex wrap>{generateDTMFRow(["7", "8", "9"])}</Flex>
            <Flex wrap>{generateDTMFRow(["*", "0", "#"])}</Flex>
          </Box>

          <Box paddingTop="space30" paddingBottom="space50">
            <Grid spacing="space30" gutter="space30">
              <Column span={isIncomingCall ? 6 : !isCallInProgress ? 12 : 0}>
                {isIncomingCall ? (
                  <ActionButton
                    style={callBtnStyle}
                    hoverStyle={callBtnHover}
                    disabled={currentCallInfo._mediaStatus === "open"}
                    onClick={acceptCall}
                  >
                    <PhoneIcon /> Accept
                  </ActionButton>
                ) : isCallInProgress ? null : (
                  <ActionButton
                    style={callBtnStyle}
                    hoverStyle={callBtnHover}
                    disabled={!!currentCallInfo}
                    onClick={makeCall}
                  >
                    <PhoneIcon /> Call
                  </ActionButton>
                )}
              </Column>
              <Column
                span={
                  isCallInProgress && isIncomingCall
                    ? 6
                    : isCallInProgress
                    ? 12
                    : 0
                }
              >
                {(isCallInProgress || isIncomingCall) && (
                  <ActionButton
                    style={hangUpBtnStyle}
                    hoverStyle={hangUpBtnHover}
                    disabled={!currentCallInfo}
                    onClick={hangUp}
                  >
                    <HangUpIcon /> Hang up
                  </ActionButton>
                )}
              </Column>
            </Grid>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}

export default Dialer;
